import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import RetirementIncomeDonut, {
  CHART_WIDTH_SINGLE,
  CHART_WIDTH_DUAL,
  CHART_HEIGHT_DUAL,
} from '../../../../components/RetirementIncomeDonut'
import StatusPill from '../../../../components/StatusPill'
import { numberToDollars, roundPercent } from '../../../../utils/utils'
import {
  AdviceColumnHeading,
  AdviceMobileHeading,
  ChartsRow,
  ChartColumn,
  DonutBox,
  ReplacementLine,
  MobileFigures,
  Figure,
} from './style'

const replacementLabel = percent => `${roundPercent(percent)}% of today's income`
const figureFor = ({ projectedMonthlyRetirementInc, retirementIncomeGoal }) =>
  `$${numberToDollars(projectedMonthlyRetirementInc, true)} (${roundPercent(retirementIncomeGoal)}%)`

const SpacedStatusPill = styled(StatusPill)`
  margin-top: 12px;
`

function IncomeCharts({ current, recommended, modified, goalPercent, collapsed }) {
  const isModified = !!modified
  const plan = modified || recommended
  const planChartWidth = isModified ? CHART_WIDTH_DUAL : CHART_WIDTH_SINGLE

  // In modified mode, both columns use the dual-chart height so the current
  // donut aligns with the modified donut's inner ring. Recommended mode uses
  // the donuts' natural height to avoid extra space above them.
  const frameHeight = isModified ? CHART_HEIGHT_DUAL : undefined

  // Recommended plans call out the income goal; modified plans call out the
  // original recommendation for comparison.
  const planCallout = isModified
    ? {
        label: 'Our recommendation:',
        amount: recommended.projectedMonthlyRetirementInc,
        percent: roundPercent(recommended.retirementIncomeGoal),
      }
    : { label: 'Goal:', amount: recommended.targetedAftTaxIncome, percent: goalPercent }

  const renderPlanDonut = withCallout =>
    isModified ? (
      <RetirementIncomeDonut
        animate
        amount={modified.projectedMonthlyRetirementInc}
        replacementPercent={modified.retirementIncomeGoal}
        recommendedPercent={recommended.retirementIncomeGoal}
        goalCallout={withCallout ? planCallout : undefined}
      />
    ) : (
      <RetirementIncomeDonut
        animate
        amount={recommended.projectedMonthlyRetirementInc}
        replacementPercent={recommended.retirementIncomeGoal}
        goalPercent={goalPercent}
        goalCallout={withCallout ? planCallout : undefined}
      />
    )

  if (collapsed) {
    return (
      <ChartsRow>
        <ChartColumn>
          <AdviceMobileHeading>{isModified ? 'Your changes' : 'Recommended'}</AdviceMobileHeading>
          <DonutBox style={{ width: planChartWidth }}>{renderPlanDonut(false)}</DonutBox>
          <ReplacementLine active compact>
            {replacementLabel(plan.retirementIncomeGoal)}
          </ReplacementLine>
          <MobileFigures>
            {isModified && <Figure bold>{`Our recommendation: ${figureFor(recommended)}`}</Figure>}
            <Figure>{`Current: ${figureFor(current)}`}</Figure>
          </MobileFigures>
        </ChartColumn>
      </ChartsRow>
    )
  }

  return (
    <ChartsRow>
      <ChartColumn>
        <AdviceColumnHeading>CURRENT</AdviceColumnHeading>
        <DonutBox style={{ width: CHART_WIDTH_SINGLE, height: frameHeight }}>
          <RetirementIncomeDonut
            animate
            amount={current.projectedMonthlyRetirementInc}
            replacementPercent={current.retirementIncomeGoal}
            goalPercent={goalPercent}
          />
        </DonutBox>
        <ReplacementLine>{replacementLabel(current.retirementIncomeGoal)}</ReplacementLine>
        <SpacedStatusPill isOnTrack={current.isOnTrack} />
      </ChartColumn>

      <ChartColumn>
        <AdviceColumnHeading planned>
          {isModified ? 'Your changes' : 'Recommended'}
        </AdviceColumnHeading>
        <DonutBox style={{ width: planChartWidth, height: frameHeight }}>
          {renderPlanDonut(true)}
        </DonutBox>
        <ReplacementLine active>{replacementLabel(plan.retirementIncomeGoal)}</ReplacementLine>
        <SpacedStatusPill isOnTrack={plan.isOnTrack} />
      </ChartColumn>
    </ChartsRow>
  )
}

const casePropType = PropTypes.shape({
  isOnTrack: PropTypes.bool,
  retirementIncomeGoal: PropTypes.number,
  projectedMonthlyRetirementInc: PropTypes.number,
  targetedAftTaxIncome: PropTypes.number,
})

IncomeCharts.propTypes = {
  current: casePropType.isRequired,
  recommended: casePropType.isRequired,
  modified: casePropType,
  goalPercent: PropTypes.number,
  collapsed: PropTypes.bool,
}

export default IncomeCharts
